import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter, faReceipt, faArrowLeft, faCircleCheck } from '@fortawesome/pro-light-svg-icons';
import { Button } from '@shared/components/ui/button';
import { ConfirmModal, type ConfirmModalRef } from '@shared/components/ui/ConfirmModal';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Badge } from '@shared/components/ui/badge';
import { Input } from '@shared/components/ui/input';
import { useImportOrder } from '../../hooks';
import { ImportOrderLineItem } from '../interaction/ImportOrderLineItem';
import type { ShopsModel } from '../../models/Shops';

export interface ImportOrderMobileModalRef {
  open: (shop: ShopsModel) => void;
}

export const ImportOrderMobileModal = forwardRef<ImportOrderMobileModalRef>((_, ref) => {
  const { t } = useTranslation('shopsModule');
  const modalRef = useRef<ModalCenterRef>(null);
  const confirmImportRef = useRef<ConfirmModalRef>(null);
  const [shopCtx, setShopCtx] = useState<{
    shopId?: number;
    channelId?: number;
  }>({});
  const [isReadyToLoad, setIsReadyToLoad] = useState(false);

  // ========================================
  // HOOK UNIFICADO PARA IMPORTACIÓN DE ÓRDENES
  // ========================================

  const {
    searchQuery,
    searchResults,
    selectedOrder,
    emptyStateImage,
    handleSearchChange,
    handleOrderSelect,
    handleBackToSearch,
    resetState,
    loadExternalOrdersOnce,
    isLoading,
    isImporting,
    noOrdersFound,
    handleImportOrder,
  } = useImportOrder({
    shopId: shopCtx.shopId,
    channelId: shopCtx.channelId,
  });

  const handleRequestImport = useCallback(() => {
    if (!selectedOrder) return;
    confirmImportRef.current?.open(
      () => handleImportOrder(),
      undefined,
      t('modals.importOrder.confirmImport.title'),
      t('modals.importOrder.confirmImport.message', { ref: selectedOrder.id }),
      t('modals.importOrder.confirmImport.confirm'),
      t('modals.importOrder.cancelButton'),
    );
  }, [handleImportOrder, selectedOrder, t]);

  const emptyListMessage = isLoading
    ? t('modals.importOrder.loading')
    : noOrdersFound
      ? t('modals.importOrder.noOrdersToImport')
      : searchQuery.trim() && searchResults.length === 0
        ? t('modals.importOrder.noSearchResults')
        : '';

  useImperativeHandle(ref, () => ({
    open: (shop: ShopsModel) => {
      // Guardar contexto de tienda
      setShopCtx({ shopId: shop.id, channelId: shop.channel_id });
      // Resetear estado usando el hook
      resetState();
      // Abrir el modal y marcar como listo para cargar
      modalRef.current?.openModal();
      setIsReadyToLoad(true);
    },
  }));

  // Usar un efecto para disparar la carga después de que el estado se actualice
  useEffect(() => {
    if (isReadyToLoad) {
      loadExternalOrdersOnce();
      setIsReadyToLoad(false); // Resetear el gatillo
    }
  }, [isReadyToLoad, loadExternalOrdersOnce]);

  return (
    <>
      <ModalCenter
        ref={modalRef}
        title={t('modals.importOrder.title')}
        type="default"
        width="lg"
        className="bg-body-bg !outline-0"
        fullScreenMobile={true}
      >
        <div className="flex flex-col gap-3 h-full min-h-0 pt-2">
          {!selectedOrder ? (
            // ========================================
            // PANTALLA 1: BÚSQUEDA DE ÓRDENES
            // ========================================
            <>
              <section className="shrink-0">
                <div className="relative">
                  <FontAwesomeIcon
                    icon={faFilter}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-subtext z-10 pointer-events-none"
                    aria-hidden
                  />
                  <Input
                    placeholder={t('modals.importOrder.filterPlaceholder')}
                    className="pl-10 text-sm"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    data-testid="order-search-input"
                    disabled={isLoading || noOrdersFound}
                    aria-busy={isLoading}
                    autoComplete="off"
                    aria-label={t('modals.importOrder.filterPlaceholder')}
                  />
                </div>
              </section>

              {/* Search Results Section - Sección de resultados de búsqueda */}
              <section className="flex-1 min-h-[200px] overflow-y-auto">
                {searchResults.length > 0 ? (
                  <div className="space-y-0 border border-card-border rounded-md bg-card-bg overflow-hidden">
                    {searchResults.map((result) => (
                      <div key={result.id} className="!mt-0">
                        <div
                          className={`flex justify-between items-center gap-2 border-b border-card-border px-4 py-3 text-sm last:border-b-0 ${
                            result.imported
                              ? 'cursor-not-allowed opacity-65 bg-card-bg/80'
                              : 'cursor-pointer hover:bg-hover-press'
                          }`}
                          data-testid={`order-result-item-${result.id}`}
                          data-imported={result.imported ? 'true' : undefined}
                          aria-disabled={result.imported}
                          title={result.imported ? t('modals.importOrder.alreadyImported') : undefined}
                          onClick={() => {
                            if (!result.imported) handleOrderSelect(result.id);
                          }}
                          role={result.imported ? undefined : 'button'}
                          tabIndex={result.imported ? -1 : 0}
                          onKeyDown={(e) => {
                            if (result.imported) return;
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleOrderSelect(result.id);
                            }
                          }}
                        >
                          <span className={`truncate font-medium ${result.imported ? 'text-subtext' : ''}`}>
                            {result.id}
                          </span>
                          <div className="flex shrink-0 items-center gap-2">
                            {result.imported ? (
                              <span className="flex items-center gap-1 text-xs text-success">
                                <FontAwesomeIcon icon={faCircleCheck} className="!h-3.5 !w-3.5" aria-hidden />
                                {t('modals.importOrder.alreadyImported')}
                              </span>
                            ) : null}
                            <Badge
                              variant="secondary"
                              className="shrink-0 px-3 py-1 bg-card-border text-xs"
                              title={t('modals.importOrder.linesCount', {
                                count: result.count,
                              })}
                            >
                              {t('modals.importOrder.linesCount', {
                                count: result.count,
                              })}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center text-center py-12 px-2">
                    <img src={emptyStateImage} alt="" className="w-28 h-28 mb-4 opacity-90" />
                    {emptyListMessage ? <p className="text-sm text-subtext max-w-sm">{emptyListMessage}</p> : null}
                  </div>
                )}
              </section>
            </>
          ) : (
            // ========================================
            // PANTALLA 2: DETALLES DE LA ORDEN
            // ========================================
            <>
              <section className="space-y-2 shrink-0">
                <div className="flex flex-wrap items-center gap-2 py-2 px-3 bg-card-bg rounded-md border border-card-border">
                  <FontAwesomeIcon icon={faReceipt} className="w-4 h-4 text-icons shrink-0" aria-hidden />
                  <span className="text-sm font-medium text-foreground truncate flex-1 min-w-0">
                    {selectedOrder.id}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="shrink-0 h-8 px-2 text-xs text-primary"
                    onClick={handleBackToSearch}
                    data-testid="change-order-button"
                  >
                    <FontAwesomeIcon icon={faArrowLeft} className="w-3.5 h-3.5 mr-1" />
                    {t('modals.importOrder.changeOrder')}
                  </Button>
                </div>
              </section>

              {/* Articles Section - Sección de artículos */}
              <section className="flex-1 min-h-0 overflow-y-auto">
                <div className="mb-3 flex items-baseline justify-between gap-2">
                  <h4 className="text-sm font-semibold text-foreground">{t('modals.importOrder.articles')}</h4>
                  <span className="text-xs tabular-nums text-subtext">{selectedOrder.items.length}</span>
                </div>

                <div className="divide-y divide-border overflow-hidden rounded-md border border-card-border bg-card-bg">
                  {selectedOrder.items.map((item) => (
                    <div key={item.id} className="px-3 py-3" data-testid={`order-item-${item.id}`}>
                      <ImportOrderLineItem item={item} quantity={item.quantity} data-testid={`product-${item.id}`} />
                    </div>
                  ))}
                </div>
              </section>

              <section className="shrink-0 pt-2 border-t border-border">
                <Button
                  variant="primary"
                  size="lg"
                  className="w-full"
                  data-testid="import-order-confirm-button"
                  onClick={handleRequestImport}
                  disabled={isImporting}
                >
                  {isImporting ? t('common.loading') : t('modals.importOrder.importButton')}
                </Button>
              </section>
            </>
          )}
        </div>
      </ModalCenter>
      <ConfirmModal ref={confirmImportRef} />
    </>
  );
});

ImportOrderMobileModal.displayName = 'ImportOrderMobileModal';
